import React from 'react';
import LocationPageLayout from '../../components/seo/LocationPageLayout';
import { getGallery } from '../../seo/galleryConfig';
import {
  TOWN_PROFILES,
  PLUMBER_BODY_INTRO,
  HANDYMAN_BODY_INTRO,
  PLUMBER_FAQS,
  HANDYMAN_FAQS,
} from '../../seo/locationContent';

// =============================================================================
// Plumber location pages
// =============================================================================

const PlumberLocationBody = ({ town }) => (
  <>
    <h2>{town.name} Plumbing Services — What Makes Us Different</h2>
    <p>{PLUMBER_BODY_INTRO(town.name)}</p>

    <h2>About The Local Property Stock</h2>
    <p>{town.propertyNotes}</p>
    <p>{town.typicalIssues}</p>

    <h2>Areas We Cover Within {town.name}</h2>
    <p>
      Our {town.name} plumbing service covers all of the {town.postcodes.join(', ')} postcode areas, including key neighbourhoods such as{' '}
      {town.landmarks.join(', ')}. If your address is on the edge of these areas we will still gladly attend — give us a call to confirm.
    </p>

    <h2>What We Do Locally</h2>
    <ul>
      <li>Leak detection and repair in older lead and copper pipework.</li>
      <li>Tap, mixer, and ceramic-disc cartridge replacements.</li>
      <li>Toilet repairs and full replacements (including macerator units).</li>
      <li>Blocked drains and waste-pipe clearance.</li>
      <li>Radiator and heating-system maintenance.</li>
      <li>Kitchen and bathroom plumbing fit-outs.</li>
      <li>Emergency 24/7 callouts to {town.name} for burst pipes and major leaks.</li>
    </ul>

    <h2>Booking a {town.name} Plumber</h2>
    <p>
      Call <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a> for immediate help. You can also message us on WhatsApp using
      the green button at the bottom of the page, or use the contact form lower down. For full details of our plumbing offering see our{' '}
      <a href="/services/plumbing">plumbing services page</a>; for out-of-hours work see our{' '}
      <a href="/services/emergency-plumber">emergency plumber service</a>.
    </p>
  </>
);

const HandymanLocationBody = ({ town }) => (
  <>
    <h2>Handyman Services in {town.name}</h2>
    <p>{HANDYMAN_BODY_INTRO(town.name)}</p>

    <h2>About The Local Property Stock</h2>
    <p>{town.propertyNotes}</p>

    <h2>Areas Covered</h2>
    <p>
      Our {town.name} handyman service covers all of {town.postcodes.join(', ')} including{' '}
      {town.landmarks.join(', ')} and surrounding neighbourhoods.
    </p>

    <h2>Typical Handyman Jobs We Book In {town.name}</h2>
    <ul>
      <li>Flat-pack assembly — wardrobes, beds, desks, kitchen units.</li>
      <li>TV wall-mounting on plasterboard or solid brick.</li>
      <li>Picture, mirror and shelf hanging — including full gallery walls.</li>
      <li>Internal door adjustments, latch and handle replacements.</li>
      <li>Lock changes — Yale, mortice, uPVC multi-point.</li>
      <li>Silicone re-sealing around baths, basins, showers, kitchen worktops.</li>
      <li>Curtain pole, blind, and bracket fitting.</li>
      <li>Painting and decorating touch-ups, end-of-tenancy reinstatements.</li>
      <li>Minor carpentry — shelves, skirting, architraves, simple cabinet repairs.</li>
    </ul>

    <h2>Pricing in {town.name}</h2>
    <p>
      Hourly rate with a one-hour minimum, then 15-minute increments after. Day-rate discount available for half-day or full-day bookings.
      Many {town.name} landlords and lettings agents use our half-day "voids" service between tenancies to clear a snag list in one
      efficient visit.
    </p>

    <h2>Book A {town.name} Handyman</h2>
    <p>
      Call <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a>, send a WhatsApp, or use the form below. For the full scope of
      what we do see our <a href="/services/handyman">main handyman services page</a> and our{' '}
      <a href="/services/property-maintenance">property maintenance service</a>.
    </p>
  </>
);

// =============================================================================
// Exported page components
// =============================================================================

const buildPlumberPage = (townKey) => {
  const town = TOWN_PROFILES[townKey];
  return () => (
    <LocationPageLayout
      townName={town.name}
      townSlug={town.slug}
      serviceKey="plumber"
      serviceLabel="Plumber"
      coreServiceSlug="plumbing"
      postcodes={town.postcodes}
      localLandmarks={town.landmarks}
      region={town.region}
      faqs={PLUMBER_FAQS(town.name)}
      galleryImages={getGallery(`plumber-${town.slug}`)}
      seoTitle={`Plumber in ${town.name}, ${town.region} | Local Plumber | H.A.R.P Solutions LTD`}
      seoDescription={`Trusted local plumber in ${town.name}, ${town.region}. Emergency callouts, leak repairs, taps, toilets, drains and bathroom plumbing across ${town.postcodes.join(', ')}. Call +44 330 122 1094.`}
    >
      <PlumberLocationBody town={town} />
    </LocationPageLayout>
  );
};

const buildHandymanPage = (townKey) => {
  const town = TOWN_PROFILES[townKey];
  return () => (
    <LocationPageLayout
      townName={town.name}
      townSlug={town.slug}
      serviceKey="handyman"
      serviceLabel="Handyman"
      coreServiceSlug="handyman"
      postcodes={town.postcodes}
      localLandmarks={town.landmarks}
      region={town.region}
      faqs={HANDYMAN_FAQS(town.name)}
      galleryImages={getGallery(`handyman-${town.slug}`)}
      seoTitle={`Handyman in ${town.name}, ${town.region} | Local Handyman | H.A.R.P Solutions LTD`}
      seoDescription={`Reliable local handyman in ${town.name}, ${town.region}. Flat-pack, wall-mounting, door repairs, silicone resealing, minor carpentry. Call +44 330 122 1094.`}
    >
      <HandymanLocationBody town={town} />
    </LocationPageLayout>
  );
};

export const PlumberRochester = buildPlumberPage('rochester');
export const PlumberGillingham = buildPlumberPage('gillingham');
export const PlumberChatham = buildPlumberPage('chatham');
export const PlumberMaidstone = buildPlumberPage('maidstone');
export const HandymanRochester = buildHandymanPage('rochester');
export const HandymanGillingham = buildHandymanPage('gillingham');
export const HandymanChatham = buildHandymanPage('chatham');
